import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, MapPinned, Clock3, ScrollText, MessageCircle, Sparkles } from "lucide-react";
import { CinematicHero } from "@/components/CinematicHero";
import { PackageRow } from "@/components/PackageCard";
import { Reveal } from "@/components/Reveal";
import { Newsletter } from "@/components/Newsletter";
import { GoogleReviews } from "@/components/GoogleReviews";
import { api } from "@/lib/apiClient";
import { DESTINATIONS, EVENT_SERVICES, IMAGES, SITE, waLink } from "@/data/site";
import Seo, { orgJsonLd } from "@/components/Seo";
import { Star } from "lucide-react";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [stories, setStories] = useState([]);

  useEffect(() => {
    api.get("/itineraries", { params: { published: true, featured: true } })
      .then((r) => setFeatured(r.data.slice(0, 4)))
      .catch(() => {});
    api.get("/stories", { params: { published: true } })
      .then((r) => setStories(r.data.slice(0, 3)))
      .catch(() => {});
  }, []);

  return (
    <div>
      <Seo title="Char Dham, Kashmir, Ladakh & Himalayan Tours from Haridwar"
        description="VacanzaGo — Haridwar's premium travel, pilgrimage & event company since 2016. Char Dham, Kashmir, Ladakh, Spiti, Goa & destination weddings. Thoughtfully planned journeys."
        jsonLd={orgJsonLd} />
      <CinematicHero />

      {/* Intro / promise */}
      <section className="bg-[#FFF7EC] py-20 lg:py-32">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <p className="eyebrow">Since {SITE.since} · {SITE.city}</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight mt-5 leading-[1.1] text-[#2A1808]">
              We don't sell packages.<br />We craft memories you keep.
            </h2>
            <p className="mt-7 text-lg text-[#553204] leading-relaxed">
              From the four shrines of Char Dham to the parikrama of Mount Kailash, from Kashmir's
              still lakes to Ladakh's high passes — we handle hotels, transfers, permits and every
              small detail. You simply arrive, and feel it.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-[#553204] text-[#F9EFE1]">
        <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { n: "2016", l: "Serving travellers since" },
            { n: "Pan-India", l: "Destination coverage" },
            { n: "1 day", l: "Quote turnaround" },
            { n: "100%", l: "Itemised, transparent quotes" },
          ].map((s, i) => (
            <Reveal key={s.l} delay={i * 80}>
              <div className="font-display text-3xl sm:text-4xl font-semibold text-white">{s.n}</div>
              <div className="mt-1 text-xs sm:text-sm text-[#F9EFE1]/70">{s.l}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Featured journeys */}
      <section className="bg-[#FFF7EC] py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal className="flex items-end justify-between flex-wrap gap-4 mb-16">
            <div>
              <p className="eyebrow">Curated for you</p>
              <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight mt-3 text-[#2A1808]">Signature journeys</h2>
            </div>
            <Link to="/packages" data-testid="home-view-all-packages" className="inline-flex items-center gap-2 text-sm font-semibold text-[#F49E1A] link-underline">
              View all journeys <ArrowRight size={16} />
            </Link>
          </Reveal>

          <div className="space-y-20 lg:space-y-28">
            {featured.map((item, i) => (
              <Reveal key={item.slug}>
                <PackageRow item={item} index={i} />
              </Reveal>
            ))}
            {featured.length === 0 && (
              <p className="text-center text-[#8A6B47]">Loading journeys…</p>
            )}
          </div>
        </div>
      </section>

      {/* Destinations */}
      <section className="bg-[#F9EFE1] py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal className="max-w-2xl mb-14">
            <p className="eyebrow">Where we travel</p>
            <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight mt-3 text-[#2A1808]">Destinations we know intimately</h2>
            <p className="mt-4 text-[#553204]">Places we've returned to, season after season.</p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {DESTINATIONS.map((d, i) => (
              <Reveal key={d.name} delay={(i % 3) * 80}>
                <Link to={`/packages?region=${encodeURIComponent(d.region)}`} data-testid={`dest-${d.name.toLowerCase().replace(/\s+/g,'-')}`} className="group relative block h-72 rounded-md overflow-hidden img-zoom">
                  <img src={d.img} alt={d.name} className="h-full w-full object-cover" loading="lazy" />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(42,24,8,0) 40%, rgba(42,24,8,0.8) 100%)" }} />
                  <div className="absolute bottom-0 p-6">
                    <p className="text-[#F49E1A] text-xs uppercase tracking-widest">{d.region}</p>
                    <h3 className="font-display text-3xl text-white mt-1">{d.name}</h3>
                    <p className="text-[#F9EFE1]/80 text-sm mt-1 italic font-display text-lg">{d.line}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* How we take over */}
      <section className="bg-[#FFF7EC] py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal className="max-w-2xl mb-14">
            <p className="eyebrow">The details matter</p>
            <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight mt-3 text-[#2A1808]">Nothing left for you to worry about</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { icon: ShieldCheck, t: "Verified stays", d: "Hand-picked, vetted hotels — no surprises on arrival." },
              { icon: MapPinned, t: "Full ground support", d: "Permits, darshan, transfers and local coordination handled." },
              { icon: Clock3, t: "Transparent pricing", d: "Itemised quotes within a day. You always know what you pay for." },
              { icon: ScrollText, t: "Crafted itineraries", d: "Paced sensibly, refined over nearly a decade of operations." },
            ].map((f, i) => (
              <Reveal key={f.t} delay={i * 80}>
                <div className="rounded-md border border-[#553204]/10 bg-[#FFFBF5] p-8 h-full">
                  <f.icon size={28} className="text-[#F49E1A]" />
                  <h3 className="font-display text-2xl mt-5 text-[#2A1808]">{f.t}</h3>
                  <p className="mt-2 text-sm text-[#8A6B47] leading-relaxed">{f.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Recommender teaser */}
      <section className="relative py-24 lg:py-36 overflow-hidden">
        <img src={IMAGES.heroMountain} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-[#361E02]/75" />
        <div className="relative max-w-3xl mx-auto px-6 text-center text-white">
          <Reveal>
            <Sparkles size={30} className="mx-auto text-[#F49E1A]" />
            <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight mt-5">Not sure where to begin?</h2>
            <p className="mt-4 text-[#F9EFE1]/85 text-lg">
              Answer a few questions and our itinerary planner will match journeys from our own
              inventory — then connect you to a real expert on WhatsApp.
            </p>
            <Link to="/recommender" data-testid="home-recommender-cta" className="inline-flex items-center gap-2 mt-8 rounded-full btn-orange px-8 py-3.5 text-sm">
              Plan My Journey <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Events vertical */}
      <section className="bg-[#361E02] text-[#F9EFE1] py-20 lg:py-32" data-testid="home-events-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="eyebrow text-[#D4AF37]">Event Management — a new vertical by VacanzaGo</p>
            <h2 className="font-display text-4xl sm:text-6xl font-medium tracking-tight mt-4 leading-[1.05] text-white">
              You bring the occasion.<br />We handle the experience.
            </h2>
            <p className="mt-6 text-[#F9EFE1]/80 leading-relaxed">
              From an intimate puja to a destination wedding or a complete corporate event — décor,
              makeup, photography, catering, entertainment and guest logistics, all under one team.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3 text-sm text-[#F9EFE1]/75">
              {EVENT_SERVICES.slice(0, 6).map((s) => (
                <span key={s.title} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#F49E1A]" />{s.title}</span>
              ))}
            </div>
            <Link to="/events" data-testid="home-events-cta" className="inline-flex items-center gap-2 mt-9 rounded-full btn-orange px-8 py-3.5 text-sm">
              Explore Event Management <ArrowRight size={16} />
            </Link>
          </Reveal>
          <Reveal delay={120} className="grid grid-cols-2 gap-4">
            <img src={IMAGES.eventMandap} alt="Wedding mandap décor" className="rounded-md object-cover h-64 w-full col-span-2" loading="lazy" />
            <img src={IMAGES.eventCandles} alt="Elegant table setting" className="rounded-md object-cover h-48 w-full" loading="lazy" />
            <img src={IMAGES.eventFloral} alt="Floral décor" className="rounded-md object-cover h-48 w-full" loading="lazy" />
          </Reveal>
        </div>
      </section>

      {/* Customer stories */}
      <GoogleReviews />

      {stories.length > 0 && (
        <section className="bg-[#F9EFE1] py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
            <Reveal className="max-w-2xl mb-14">
              <p className="eyebrow">In their words</p>
              <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight mt-3 text-[#2A1808]">Journeys, remembered</h2>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {stories.map((s, i) => (
                <Reveal key={s.id} delay={(i % 3) * 80}>
                  <div className="rounded-md border border-[#553204]/10 bg-[#FFFBF5] overflow-hidden h-full flex flex-col">
                    <div className="img-zoom aspect-[3/2] overflow-hidden"><img src={s.image} alt={s.family_name} className="h-full w-full object-cover" loading="lazy" /></div>
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="flex gap-1 mb-2">{Array.from({ length: s.rating || 5 }).map((_, k) => <Star key={`star-${k}`} size={14} className="fill-[#F49E1A] text-[#F49E1A]" />)}</div>
                      <blockquote className="font-display text-xl text-[#2A1808] leading-snug">"{s.quote}"</blockquote>
                      <p className="mt-auto pt-4 text-sm text-[#8A6B47]">{s.family_name} · {s.destination}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <div className="mt-10"><Link to="/stories" className="inline-flex items-center gap-2 text-sm font-semibold text-[#F49E1A] link-underline">Read more stories →</Link></div>
          </div>
        </section>
      )}

      <Newsletter />

      {/* Final CTA */}
      <section className="bg-[#FFF7EC] py-20 lg:py-28">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-[#2A1808]">Have a destination in mind?</h2>
            <p className="mt-4 text-[#553204] text-lg">Tell us your group, dates and what matters most. We'll send a tailored, itemised plan within one business day — no obligation.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/recommender" className="rounded-full btn-orange px-7 py-3 text-sm">Customise a Trip</Link>
              <a href={waLink()} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-[#553204]/25 text-[#2A1808] px-7 py-3 text-sm hover:bg-[#553204]/5 transition-colors">
                <MessageCircle size={16} /> {SITE.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
